/**
 * Single source of truth for which routes cannot function without Clerk.
 *
 * `proxy.ts` feeds these pattern arrays into `createRouteMatcher` for its
 * auth gates, and the client-side Clerk load failure boundary
 * (`components/auth/clerk-load-failure-boundary.tsx`) uses
 * `isClerkCriticalPathname` to decide between the blocking recovery flow
 * (auth-critical routes) and content-first degradation (public marketing /
 * content routes, where a failed `clerk.brew.new` script must never hide
 * the page). Keeping both sides on the same arrays prevents drift.
 *
 * This module is imported by client code — it must stay free of
 * `server-only` imports.
 */

import { splitLocalePrefix } from '@/lib/i18n/config'
import { parseTemplatePublicPath } from '@/lib/templates/public-path'

/** Routes whose entire purpose is Clerk-rendered auth UI. */
export const AUTH_ONLY_ROUTE_PATTERNS = [
  '/sign-in(.*)',
  '/sign-up(.*)',
  '/create-organization(.*)',
]

/**
 * `(app)` page segments that the proxy authenticates inline instead of
 * via `auth.protect()` (`/templates`, `/flows` and `/integrations` bounce
 * signed-out visitors to `/browse/...`).
 */
export const PROXY_INLINE_AUTH_SEGMENTS = [
  'templates',
  'flows',
  'integrations',
] as const

/** Signed-in product routes gated by the proxy (`auth.protect()`). */
export const PRIVATE_ROUTE_PATTERNS = [
  '/home(.*)',
  '/overview(.*)',
  '/emails(.*)',
  '/automations(.*)',
  '/triggers(.*)',
  '/audience(.*)',
  '/analytics(.*)',
  '/events(.*)',
  '/insights(.*)',
  '/brand(.*)',
  '/brand-extraction(.*)',
  '/assets(.*)',
  '/domains(.*)',
  '/plan(.*)',
  '/chat(.*)',
  '/chats(.*)',
  // Chat-app account linking (`(app)/channels/link`): a signed-out person
  // opening the link from Slack signs in first, then lands back on it.
  '/channels(.*)',
  '/usecases(.*)',
  // `(onboarding)/setup/page.tsx` calls `getOrgContext()` (throws
  // "User not authenticated" with no session). Same `cacheComponents`
  // layout-redirect race as `/settings` below — gate it at the proxy so
  // unauthenticated hits never reach the page handler.
  '/setup(.*)',
  // The post-auth landing (`app/(auth)/continue/route.ts`): the proxy sends a
  // signed-out hit to sign-in and an org-less one to `/create-organization`,
  // so the handler only ever classifies an active organization.
  '/continue(.*)',
  // Every page under `(app)/settings/**` calls `getOrgContext()`
  // (see `lib/auth-org.ts`), which throws `"User not authenticated"`
  // when the request has no Clerk session. Under Next 16
  // `cacheComponents`, the `(app)/layout.tsx` redirect can race with
  // page render, so the throw still hits production logs. Gate the
  // routes at the proxy layer instead so unauthenticated visitors are
  // sent through Clerk before the page handler ever runs.
  '/settings(.*)',
]

/**
 * Routes that need a working Clerk client but are intentionally NOT
 * proxy-gated: `/oauth/authorize` handles its own sign-in redirect, and
 * `/templates` bounces unauthenticated visitors to `/browse/templates`
 * inline in the proxy. A template's readable `/templates/{brand}/{title}`
 * is the exception (see `isClerkCriticalPathname`).
 */
const ADDITIONAL_CLERK_CRITICAL_PATTERNS = [
  '/oauth/authorize(.*)',
  '/templates(.*)',
]

// `createRouteMatcher` patterns here are all `<prefix>` or `<prefix>(.*)`;
// reduce them to plain prefixes for the client-side check.
const CLERK_CRITICAL_PREFIXES = [
  ...AUTH_ONLY_ROUTE_PATTERNS,
  ...PRIVATE_ROUTE_PATTERNS,
  ...ADDITIONAL_CLERK_CRITICAL_PATTERNS,
].map((pattern) => pattern.replace(/\(\.\*\)$/, ''))

/**
 * True when the page at `pathname` cannot do its job without Clerk, so a
 * Clerk asset load failure should trigger the blocking auto-retry /
 * recovery flow. Everything else (blog, pricing, browse, unsubscribe, …)
 * is content-first: the page stays visible and sign-in affordances
 * degrade quietly.
 *
 * A template's readable `/templates/{brand}/{title}` is content-first too:
 * signed out, the proxy serves the public marketing page behind that URL,
 * which must stay readable when Clerk fails, exactly as `/browse/*` does.
 */
export function isClerkCriticalPathname(pathname: string): boolean {
  // `/de/sign-in` is the sign-in page too.
  const { path } = splitLocalePrefix(pathname)
  if (
    path.startsWith('/templates/') &&
    parseTemplatePublicPath(path.slice('/templates/'.length)) !== null
  ) {
    return false
  }
  return CLERK_CRITICAL_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  )
}
